
import { useState, useEffect, useMemo, useRef } from "react";
import { useTheme } from "./App";
import { supabase, getVendor, fetchAllMarketIntelReports } from "./supabase";
import CONFIG from "./config";
import {
  SHARED_MARKET_INTEL_VENDOR_ID, calcMomentumScore, calcStabilityScore, calcDivergenceScore,
  calcMasterScore, scoreLabel, opportunityTier, getPokemonTier, detectMarketPhase, PHASE_META,
} from "./marketIntelLogic";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell, Legend,
} from "recharts";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const OPPORTUNITY_MIN_SCORE = 60;

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else cur += line[i];
    }
    vals.push(cur.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').replace(/^"|"$/g, ''); });
    return obj;
  }).filter(r => r['Rank'] && r['Product Name']);
}

function parsePrice(s) {
  return parseFloat((s || '').replace(/[$,]/g, '')) || 0;
}

function parseMonth(s) {
  if (!s) return "";
  if (s.match(/^\d{4}-\d{2}$/)) return s;
  const months = ["january","february","march","april","may","june",
    "july","august","september","october","november","december"];
  const parts = s.toLowerCase().split(/[\s,]+/);
  const mi = months.findIndex(m => parts[0].startsWith(m));
  if (mi >= 0 && parts[1]) return `${parts[1]}-${String(mi+1).padStart(2,'0')}`;
  return s;
}

// ── SCORING FORMULAS, PHASE DETECTION, AND OPPORTUNITY TIER ──────────────────

// ─── VERDICT BUILDER ─────────────────────────────────────────────────────────
function buildVerdict(c) {
  const baseLabel = c.opportunity?.label ?? null;
  const lines     = [];
  const prev      = c.prev;
  const hasPrev   = prev != null && prev.rank != null && prev.avg_price != null;
  const phase     = c.marketPhase || "insufficient_data";
  const phaseMeta = PHASE_META[phase] || PHASE_META.insufficient_data;
  const pokeTier  = getPokemonTier(c.product_name);

  let verdict = baseLabel;
  if (baseLabel === "AVOID" && phaseMeta.overrideAvoid) {
    verdict = pokeTier?.tier === 1 ? "BUY" : "WATCH";
  }
  if (phase === "in_freefall") verdict = pokeTier ? "DIP" : "AVOID";

  // ── Market phase headline ─────────────────────────────────────────────────
  const setEntry  = CONFIG.POKEMON_SETS.find(s => s.name?.toLowerCase() === (c.set_name || "").toLowerCase());
  const daysOld   = setEntry?.released
    ? Math.floor((Date.now() - new Date(setEntry.released).getTime()) / 86400000)
    : null;

  if (phase === "post_release_dip") {
    const ageStr = daysOld !== null ? `${daysOld} days since release` : "recently released";
    lines.push(`${phaseMeta.emoji} This card is showing a classic post-release correction. The set is ${ageStr} and new supply is flooding the market, driving prices down temporarily. This is expected behavior and not a sign of lost interest.`);
  } else if (phase === "floor_forming") {
    lines.push(`${phaseMeta.emoji} The rate of price decline is slowing. this card may be approaching a price floor. Early buyers at this stage often capture the best value before stabilization.`);
  } else if (phase === "recovering") {
    lines.push(`${phaseMeta.emoji} Price is recovering from a prior low. this card has already found its floor and demand is returning.`);
  } else if (phase === "in_freefall") {
    lines.push(`${phaseMeta.emoji} Price has declined for ${c.history.length} consecutive months with no slowdown. genuine loss of market interest.`);
  } else if (phase === "rising") {
    lines.push(`${phaseMeta.emoji} Price and rank are trending upward. active demand momentum.`);
  } else if (phase === "mature_stable") {
    lines.push(`${phaseMeta.emoji} Price and rank have been stable for ${c.history.length} months. this card has found its market equilibrium.`);
  }

  // ── Pokémon popularity context ────────────────────────────────────────────
  if (pokeTier?.tier === 1) {
    lines.push(`⭐ ${pokeTier.name} is a Tier 1 evergreen collector target with proven long-term grading demand. Price dips on Tier 1 Pokémon historically recover. this is a strong buy-the-dip candidate.`);
  } else if (pokeTier?.tier === 2) {
    lines.push(`🔶 ${pokeTier.name} is a Tier 2 popular Pokémon. solid collector demand but more sensitive to set cycles and meta relevance. Weigh the market phase carefully before buying.`);
  }

  if (!hasPrev) {
    if (c.rank <= 10) {
      lines.push(`Ranked #${c.rank} this month. top-10 demand with no prior data to compare yet.`);
    } else {
      lines.push(`Currently ranked #${c.rank}. upload a second month to unlock full trend analysis.`);
    }
    return { lines, verdict };
  }

  // ── Rank movement ─────────────────────────────────────────────────────────
  const rankChange = c.rankChange ?? (prev.rank - c.rank);
  if (rankChange > 15) {
    lines.push(`Jumped ${rankChange} spots in rank (#${prev.rank} → #${c.rank}). strong upward demand momentum.`);
  } else if (rankChange > 0) {
    lines.push(`Rank improved by ${rankChange} (#${prev.rank} → #${c.rank}) month over month.`);
  } else if (rankChange < -15) {
    lines.push(`Fell ${Math.abs(rankChange)} spots in rank (#${prev.rank} → #${c.rank}). notable demand drop.`);
  } else if (rankChange < 0) {
    lines.push(`Rank slipped ${Math.abs(rankChange)} positions (#${prev.rank} → #${c.rank}) this month.`);
  } else {
    lines.push(`Rank held steady at #${c.rank}. no movement from last month.`);
  }

  // ── Price movement ────────────────────────────────────────────────────────
  const prevPrice = parseFloat(prev.avg_price) || 0;
  const currPrice = parseFloat(c.avg_price)    || 0;
  const priceDiff = currPrice - prevPrice;
  const pricePct  = prevPrice > 0 ? (priceDiff / prevPrice) * 100 : 0;
  if (Math.abs(pricePct) >= 1) {
    if (pricePct > 0 && rankChange > 0) {
      lines.push(`Price rose ${pricePct.toFixed(1)}% ($${prevPrice.toFixed(2)} → $${currPrice.toFixed(2)}) alongside rank. demand is already being priced in.`);
    } else if (pricePct > 0 && rankChange <= 0) {
      lines.push(`Price rose ${pricePct.toFixed(1)}% while rank fell. possible speculative spike or thinning supply.`);
    } else if (pricePct < 0 && rankChange > 0) {
      lines.push(`Price dropped ${Math.abs(pricePct).toFixed(1)}% ($${prevPrice.toFixed(2)} → $${currPrice.toFixed(2)}) while rank improved. popularity rising faster than price, a textbook buy signal.`);
    } else {
      lines.push(`Price fell ${Math.abs(pricePct).toFixed(1)}% ($${prevPrice.toFixed(2)} → $${currPrice.toFixed(2)}) alongside weakening rank.`);
    }
  } else {
    lines.push(`Price stable at $${currPrice.toFixed(2)} (< 1% change month over month).`);
  }

  // ── Grading arbitrage note ────────────────────────────────────────────────
  if ((phase === "post_release_dip" || phase === "floor_forming") && pokeTier) {
    lines.push(`💡 Grading opportunity: buying ${pokeTier.name} cards at a price dip to submit for PSA/BGS grading can significantly increase return. graded copies of popular Pokémon consistently command a premium over raw market price.`);
  }

  // ── Divergence signal ─────────────────────────────────────────────────────
  if (c.divergence != null) {
    if (c.divergence >= 70) {
      lines.push(`Divergence score of ${c.divergence.toFixed(0)}. rank is rising faster than price, suggesting the market hasn't fully priced in demand yet.`);
    } else if (c.divergence < 40) {
      lines.push(`Divergence score of ${c.divergence.toFixed(0)}. price is running ahead of rank, indicating possible overvaluation.`);
    }
  }

  // ── Stability context ─────────────────────────────────────────────────────
  if (c.stability != null && c.totalMonths >= 2) {
    if (c.stability >= 75) {
      lines.push(`Stability score of ${c.stability.toFixed(0)} across ${c.totalMonths} months. this card consistently holds its rank position.`);
    } else if (c.stability < 40) {
      lines.push(`Stability score of ${c.stability.toFixed(0)}. rank has fluctuated significantly; higher risk hold.`);
    }
  }

  // ── Long-term trend ───────────────────────────────────────────────────────
  if (c.longRankChange != null && Math.abs(c.longRankChange) > 20) {
    lines.push(c.longRankChange > 0
      ? `Over the full ${c.totalMonths}-month window, rank improved by ${c.longRankChange} positions. sustained long-term demand trend.`
      : `Over the full ${c.totalMonths}-month window, rank declined by ${Math.abs(c.longRankChange)} positions. longer-term weakening trend.`);
  }
  if (c.longPricePct != null && Math.abs(c.longPricePct) >= 10) {
    lines.push(c.longPricePct > 0
      ? `Price is up ${c.longPricePct.toFixed(1)}% over the full tracking window. factor this into your buy cost.`
      : `Price is down ${Math.abs(c.longPricePct).toFixed(1)}% over the full tracking window. may present a discount opportunity.`);
  }

  const capitalizeSentences = (str) =>
    str.replace(/(^|[.!?]\s+)([a-z])/g, (m, p1, p2) => p1 + p2.toUpperCase());

  return {
    lines: lines.map(l => capitalizeSentences(l)),
    verdict,
  };
}

// ─── SORT OPTIONS (module-level constant. never recreated) ──────────────────
const SORT_OPTIONS = [
  { id:"rank",        label:"Rank"        },
  { id:"score",       label:"Score"       },
  { id:"price_asc",   label:"Price ↑"     },
  { id:"price_desc",  label:"Price ↓"     },
];

// ─── SORT BAR (defined outside component. stable reference, no hook violations) ─
function SortBar({ value, onChange, T }) {
  return (
    <div style={{display:"flex",gap:4,marginBottom:12,alignItems:"center"}}>
      <span style={{fontSize:11,color:T.textFaint,marginRight:4}}>Sort:</span>
      {SORT_OPTIONS.map(o => (
        <button key={o.id}
          style={{
            padding:"4px 10px", borderRadius:6, fontSize:11, cursor:"pointer",
            fontFamily:"'Sora',sans-serif", border:`1px solid ${value===o.id ? "#3b82f6" : T.border}`,
            background: value===o.id ? "rgba(59,130,246,0.15)" : "transparent",
            color: value===o.id ? "#3b82f6" : T.textMuted,
            fontWeight: value===o.id ? 600 : 400,
          }}
          onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function MarketIntelligence({ onClose, compact = false, isAdmin = false }) {
  const { S, T } = useTheme();
  const [view, setView]           = useState("dashboard");
  const [showLegend, setShowLegend] = useState(false);
  const [reports, setReports]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMonth, setUploadMonth] = useState("");
  const [uploadTier, setUploadTier]   = useState("budget");
  const [search, setSearch]       = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [activeTab, setActiveTab] = useState("opportunities");
  const [buySort,  setBuySort]  = useState("rank");
  const [oppSort,  setOppSort]  = useState("rank");
  const [avoSort,  setAvoSort]  = useState("rank");
  const [dipSort,  setDipSort]  = useState("rank");
  const fileRef = useRef(null);
  const file2Ref = useRef(null);

  const loadReports = async () => {
    setLoading(true);
    try {
      const vendor = await getVendor();
      const data = await fetchAllMarketIntelReports(SHARED_MARKET_INTEL_VENDOR_ID);
      setReports(data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadReports(); }, []);

  // ── Compute analytics from stored reports ──────────────────────────────────
  const analytics = useMemo(() => {
    if (!reports.length) return [];

    const months  = [...new Set(reports.map(r => r.report_month))].sort().reverse();
    const currMonth = months[0];
    const prevMonth = months[1] || null;

    const dedupByMonth = (rows) => {
      const seen = new Map();
      for (const r of rows) {
        const key = r.product_name?.trim().toLowerCase();
        if (!key) continue;
        if (!seen.has(key) || r.rank < seen.get(key).rank) {
          seen.set(key, r);
        }
      }
      return [...seen.values()];
    };

    const currData = dedupByMonth(reports.filter(r => r.report_month === currMonth));
    const prevData = prevMonth
      ? dedupByMonth(reports.filter(r => r.report_month === prevMonth))
      : [];

    const totalCards = currData.length;

    return currData.map(curr => {
      const rawHistory = reports.filter(r =>
        r.product_name?.trim().toLowerCase() === curr.product_name?.trim().toLowerCase()
      );

      const monthMap = new Map();
      for (const r of rawHistory) {
        const m = r.report_month;
        if (!monthMap.has(m) || r.rank < monthMap.get(m).rank) {
          monthMap.set(m, r);
        }
      }
      const history = [...monthMap.values()]
        .sort((a, b) => a.report_month.localeCompare(b.report_month));

      const totalMonths = Math.min(history.length, months.length);

      const prev = prevData.find(p =>
        p.product_name?.trim().toLowerCase() === curr.product_name?.trim().toLowerCase()
      );

      const oldest = history[0];
      const hasLongHistory = totalMonths >= 3;
      const longRankChange = hasLongHistory ? oldest.rank - curr.rank : null;
      const longPricePct   = hasLongHistory && oldest.avg_price > 0
        ? (curr.avg_price - oldest.avg_price) / oldest.avg_price * 100
        : null;

      const momentum   = calcMomentumScore(curr, prev, totalCards);
      const stability  = calcStabilityScore(history);
      const divergence = calcDivergenceScore(curr, prev);
      const master     = calcMasterScore(momentum, stability, divergence);
      const rankChange = prev ? prev.rank - curr.rank : null;
      const marketPhase = detectMarketPhase({ history, set_name: curr.set_name });
      const pokemonTier = getPokemonTier(curr.product_name);

      const partial = {
        ...curr, prev, history, momentum, stability, divergence,
        master, rankChange, longRankChange, longPricePct,
        totalMonths, marketPhase, pokemonTier,
      };

      return {
        ...partial,
        opportunity: opportunityTier(divergence, partial),
        scoreInfo:   scoreLabel(master),
        monthsData:  months.length,
      };
    }).sort((a, b) => b.master - a.master);
  }, [reports]);

  // ── Filtered by search ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return analytics;
    const q = search.toLowerCase();
    return analytics.filter(c =>
      c.product_name?.toLowerCase().includes(q) ||
      c.set_name?.toLowerCase().includes(q)
    );
  }, [analytics, search]);

  const months = [...new Set(reports.map(r => r.report_month))].sort().reverse();
  const hasMultipleMonths = months.length >= 2;

  // ── Upload handler ──────────────────────────────────────────────────────────
  const handleUpload = async (file, tier) => {
    if (!file || !uploadMonth) {
      alert("Please select a month before uploading");
      return;
    }
    setUploading(true);
    try {
      const text  = await file.text();
      const rows  = parseCsv(text);
      const month = parseMonth(uploadMonth);

      const vendor = await getVendor();

      await supabase.from("tcgplayer_reports")
        .delete()
        .eq("vendor_id", SHARED_MARKET_INTEL_VENDOR_ID)
        .eq("report_month", month)
        .eq("report_tier", tier);

      const inserts = rows.map(r => ({
        vendor_id:    SHARED_MARKET_INTEL_VENDOR_ID,
        report_month: month,
        report_tier:  tier,
        rank:         parseInt(r['Rank']) || 0,
        product_name: r['Product Name']?.trim(),
        set_name:     r['Set Name']?.trim(),
        avg_price:    parsePrice(r['Average Sale Price']),
      })).filter(r => r.rank && r.product_name);

      for (let i = 0; i < inserts.length; i += 50) {
        const { error } = await supabase.from("tcgplayer_reports").insert(inserts.slice(i, i+50));
        if (error) throw error;
      }

      await loadReports();
      setView("dashboard");
    } catch(e) {
      console.error(e);
      alert("Upload error: " + e.message);
    }
    setUploading(false);
  };

  // ── Delete a single month ───────────────────────────────────────────────────
  const handleDeleteMonth = async (month) => {
    if (!window.confirm(`Delete all data for ${month}? This cannot be undone.`)) return;
    try {
      const vendor = await getVendor();
      const { error } = await supabase.from("tcgplayer_reports")
        .delete()
        .eq("vendor_id", SHARED_MARKET_INTEL_VENDOR_ID)
        .eq("report_month", month);
      if (error) throw error;
      await loadReports();
    } catch(e) { alert("Delete error: " + e.message); }
  };

  // ── Delete ALL market intel data ────────────────────────────────────────────
  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL market intelligence data? This will wipe every month and cannot be undone.")) return;
    if (!window.confirm("Are you absolutely sure? All uploaded reports will be permanently deleted.")) return;
    try {
      const vendor = await getVendor();
      const { error } = await supabase.from("tcgplayer_reports")
        .delete()
        .eq("vendor_id", SHARED_MARKET_INTEL_VENDOR_ID);
      if (error) throw error;
      setReports([]);
      setView("dashboard");
    } catch(e) { alert("Delete error: " + e.message); }
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────
  const containerStyle = compact
    ? { width:"100%", height:"100%", overflow:"auto" }
    : { ...S.page };

  if (view === "upload") return (
    <div style={containerStyle}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>setView("dashboard")}>← Back</button>
        <div style={{fontSize:16,fontWeight:700}}>Upload TCGPlayer Report</div>
      </div>

      <div style={{...S.infoBox,marginBottom:20}}>
        <div style={{fontSize:13,color:T.textMuted,lineHeight:1.8}}>
          Upload both CSV files from TCGPlayer (the $1–$49 and $50+ reports) for the same month.
          Historical data accumulates automatically to power momentum and trend analytics.
        </div>
      </div>

      <div style={{marginBottom:16}}>
        <label style={S.label}>Report Month (e.g. "March 2026" or "2026-03")</label>
        <input style={S.input} value={uploadMonth}
          onChange={e=>setUploadMonth(e.target.value)}
          placeholder="March 2026" />
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        <div style={{...S.card,textAlign:"center",cursor:"pointer"}}
          onClick={()=>fileRef.current?.click()}>
          <div style={{fontSize:24,marginBottom:8}}>📄</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>$1 – $49 Report</div>
          <div style={{fontSize:11,color:T.textMuted}}>Top selling singles under $50</div>
          <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}}
            onChange={e=>{if(e.target.files[0])handleUpload(e.target.files[0],"budget");}} />
        </div>
        <div style={{...S.card,textAlign:"center",cursor:"pointer"}}
          onClick={()=>file2Ref.current?.click()}>
          <div style={{fontSize:24,marginBottom:8}}>💎</div>
          <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>$50+ Report</div>
          <div style={{fontSize:11,color:T.textMuted}}>Premium singles</div>
          <input ref={file2Ref} type="file" accept=".csv" style={{display:"none"}}
            onChange={e=>{if(e.target.files[0])handleUpload(e.target.files[0],"premium");}} />
        </div>
      </div>

      {uploading && (
        <div style={{textAlign:"center",padding:"20px",color:T.textMuted}}>
          Uploading…
        </div>
      )}

      {months.length > 0 && (
        <div style={S.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={S.h2}>Stored Reports ({months.length} month{months.length>1?"s":""})</div>
          </div>
          {months.map(m => (
            <div key={m} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>{m}</div>
                <div style={{fontSize:11,color:T.textMuted}}>
                  {reports.filter(r=>r.report_month===m).length} cards ·{" "}
                  {[...new Set(reports.filter(r=>r.report_month===m).map(r=>r.report_tier))].join(" + ")}
                </div>
              </div>
              <button
                style={{...S.btn("ghost"),padding:"5px 10px",fontSize:11,color:"#f87171",borderColor:"#f87171"}}
                onClick={()=>handleDeleteMonth(m)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {months.length > 0 && (
        <div style={{...S.card,borderColor:"#7f1d1d",marginTop:8}}>
          <div style={{fontSize:12,fontWeight:700,color:"#f87171",marginBottom:6}}>⚠ Danger Zone</div>
          <div style={{fontSize:12,color:T.textMuted,marginBottom:12}}>
            Wipe all market intelligence data and start fresh. This cannot be undone.
          </div>
          <button
            style={{...S.btn("danger"),width:"100%",padding:"11px"}}
            onClick={handleDeleteAll}>
            🗑 Delete All Market Data
          </button>
        </div>
      )}
    </div>
  );

  // ── Card detail view ────────────────────────────────────────────────────────
  if (view === "detail" && selectedCard) {
    const c = selectedCard;

    const allMonths = [...new Set(reports.map(r => r.report_month))].sort();
    const monthlyRows = allMonths.map(m => {
      const entry = c.history.find(h => h.report_month === m);
      return {
        month: m,
        price: entry ? entry.avg_price : null,
        rank:  entry ? entry.rank      : null,
      };
    });

    const radarData = [
      { metric:"Momentum",  value: c.momentum  ?? 0 },
      { metric:"Stability", value: c.stability ?? 0 },
      { metric:"Divergence",value: c.divergence ?? 0 },
    ];

    const shortMonth = (s) => {
      const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const m = parseInt((s || "").split("-")[1]) - 1;
      return names[m] ?? s;
    };

    return (
      <div style={containerStyle}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>setView("dashboard")}>← Back</button>
          <div style={{flex:1}}>
            <div style={{fontSize:16,fontWeight:700}}>{c.product_name}</div>
            <div style={{fontSize:12,color:T.textMuted}}>{c.set_name}</div>
          </div>
          <div style={{
            fontSize:14,fontWeight:700,padding:"6px 14px",
            borderRadius:20,
            background:c.scoreInfo.tier==="A"||c.scoreInfo.tier==="B"?"#16a34a":"#1d4ed8",
            color:"#ffffff",
          }}>
            {c.master}
          </div>
        </div>

        <div style={{...S.infoBox,marginBottom:16,
          borderColor:c.scoreInfo.color,
          background:c.scoreInfo.tier==="A"||c.scoreInfo.tier==="B"
            ?"rgba(22,163,74,0.12)":"rgba(29,78,216,0.08)"}}>
          <div style={{fontSize:14,fontWeight:700,
            color:c.scoreInfo.tier==="A"?"#15803d":
                  c.scoreInfo.tier==="B"?"#16a34a":
                  c.scoreInfo.tier==="C"?"#1d4ed8":
                  c.scoreInfo.tier==="D"?"#b45309":"#dc2626"}}>
            {c.scoreInfo.label}
          </div>
          {(() => {
            const effVerdict = buildVerdict(c).verdict;
            const vColor = effVerdict === "BUY"   ? "#15803d"
                         : effVerdict === "WATCH" ? "#1d4ed8"
                         : effVerdict === "HOLD"  ? "#b45309"
                         : effVerdict === "DIP"   ? "#0f766e"
                         : "#dc2626";
            return effVerdict ? (
              <div style={{fontSize:12,marginTop:4,fontWeight:600,color:vColor}}>
                Opportunity: {effVerdict}
              </div>
            ) : null;
          })()}
        </div>

        {(() => {
          const verdict = buildVerdict(c);
          const effLabel = verdict.verdict;
          const verdictBorder = effLabel === "BUY"   ? "#166534"
                              : effLabel === "WATCH" ? "#1d4ed8"
                              : effLabel === "HOLD"  ? "#92400e"
                              : effLabel === "AVOID" ? "#991b1b"
                              : effLabel === "DIP"   ? "#0f766e"
                              : T.border;
          const verdictBg    = effLabel === "BUY"   ? "rgba(21,128,61,0.08)"
                             : effLabel === "WATCH" ? "rgba(59,130,246,0.08)"
                             : effLabel === "HOLD"  ? "rgba(180,83,9,0.08)"
                             : effLabel === "AVOID" ? "rgba(220,38,38,0.08)"
                             : effLabel === "DIP"   ? "rgba(15,118,110,0.08)"
                             : T.surface2;
          const phaseMeta = PHASE_META[c.marketPhase] || PHASE_META.insufficient_data;
          return (
            <div style={{
              marginBottom:16, padding:"12px 14px", borderRadius:10,
              background: verdictBg, border:`1px solid ${verdictBorder}`,
            }}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{fontSize:11,fontFamily:"'DM Mono',monospace",
                  color:verdictBorder,textTransform:"uppercase",letterSpacing:"0.06em"}}>
                  {effLabel ?? "Rating"}
                </div>
                {c.marketPhase && c.marketPhase !== "insufficient_data" && (
                  <div style={{fontSize:11,color:T.textMuted,
                    padding:"1px 8px",borderRadius:4,border:`1px solid ${T.border}`}}>
                    {phaseMeta.emoji} {phaseMeta.label}
                  </div>
                )}
                {c.pokemonTier?.tier === 1 && (
                  <div style={{fontSize:11,color:"#f59e0b",fontWeight:700}}>⭐ Tier 1</div>
                )}
                {c.pokemonTier?.tier === 2 && (
                  <div style={{fontSize:11,color:"#94a3b8",fontWeight:600}}>🔶 Tier 2</div>
                )}
              </div>
              {verdict.lines.map((line, i) => (
                <div key={i} style={{
                  display:"flex", gap:8, alignItems:"flex-start",
                  marginBottom: i < verdict.lines.length - 1 ? 8 : 0,
                }}>
                  <span style={{fontSize:12, color:verdictBorder, flexShrink:0, marginTop:1}}>•</span>
                  <span style={{fontSize:13, color:T.text, lineHeight:1.55}}>{line}</span>
                </div>
              ))}
            </div>
          );
        })()}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
          <div style={S.stat}>
            <div style={S.statLbl}>Rank</div>
            <div style={S.statVal}>#{c.rank}</div>
            {c.rankChange !== null && (
              <div style={{fontSize:11,color:c.rankChange>0?"#4ade80":"#f87171"}}>
                {c.rankChange>0?`↑${c.rankChange}`:c.rankChange<0?`↓${Math.abs(c.rankChange)}`:"—"}
                <span style={{color:T.textFaint}}> vs last mo</span>
              </div>
            )}
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Avg Price</div>
            <div style={S.statVal}>${c.avg_price?.toFixed(2)}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Tracked</div>
            <div style={S.statVal}>{c.totalMonths} mo</div>
          </div>
        </div>

        {c.longRankChange !== null && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
            <div style={S.stat}>
              <div style={S.statLbl}>Rank change (all time)</div>
              <div style={{...S.statVal, fontSize:16,
                color: c.longRankChange > 0 ? "#4ade80" : c.longRankChange < 0 ? "#f87171" : T.textMuted}}>
                {c.longRankChange > 0 ? `↑${c.longRankChange}` : c.longRankChange < 0 ? `↓${Math.abs(c.longRankChange)}` : "—"}
              </div>
              <div style={{fontSize:10,color:T.textFaint}}>since {c.history[0]?.report_month}</div>
            </div>
            <div style={S.stat}>
              <div style={S.statLbl}>Price change (all time)</div>
              <div style={{...S.statVal, fontSize:16,
                color: c.longPricePct > 0 ? "#f87171" : c.longPricePct < 0 ? "#4ade80" : T.textMuted}}>
                {c.longPricePct !== null
                  ? `${c.longPricePct > 0 ? "+" : ""}${c.longPricePct.toFixed(1)}%`
                  : "—"}
              </div>
              <div style={{fontSize:10,color:T.textFaint}}>since {c.history[0]?.report_month}</div>
            </div>
          </div>
        )}
        {c.longRankChange === null && <div style={{marginBottom:16}} />}

        {/* ── Monthly breakdown table ─────────────────────────────────────── */}
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>Monthly Data</div>

          {allMonths.length > 1 && (
            <ResponsiveContainer width="100%" height={140} style={{marginBottom:12}}>
              <LineChart data={monthlyRows} margin={{top:4,right:8,left:-24,bottom:0}}>
                <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={shortMonth}
                  tick={{fontSize:9,fill:T.textMuted}} />
                <YAxis yAxisId="price" tick={{fontSize:9,fill:T.textMuted}} />
                <YAxis yAxisId="rank" orientation="right" reversed
                  tick={{fontSize:9,fill:T.textMuted}} />
                <Tooltip
                  contentStyle={{background:T.surface,border:`1px solid ${T.border}`,
                    borderRadius:8,color:T.text,fontSize:11}}
                  formatter={(val, name) => (val == null) ? ["N/A", name] : [
                    name === "Avg Price $" ? `$${Number(val).toFixed(2)}` : `#${val}`, name
                  ]}
                  labelFormatter={shortMonth}
                />
                <Line yAxisId="price" dataKey="price" stroke="#3b82f6" strokeWidth={2}
                  connectNulls={false} dot={{r:3}} name="Avg Price $" />
                <Line yAxisId="rank" dataKey="rank" stroke="#f87171" strokeWidth={2}
                  strokeDasharray="4 2" connectNulls={false} dot={{r:3}} name="Rank" />
                <Legend wrapperStyle={{fontSize:10,color:T.textMuted}} />
              </LineChart>
            </ResponsiveContainer>
          )}

          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr>
                  {["Month","Price","Rank","vs Prev Mo"].map(h => (
                    <th key={h} style={{textAlign:"left",padding:"5px 8px",
                      fontFamily:"'DM Mono',monospace",fontSize:10,
                      color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.05em",
                      borderBottom:`1px solid ${T.border}`}}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthlyRows.map((row, i) => {
                  const prev = i > 0 ? monthlyRows[i - 1] : null;
                  const priceDelta = prev != null && row.price != null && prev.price != null
                    ? row.price - prev.price : null;
                  const rankDelta  = prev != null && row.rank  != null && prev.rank  != null
                    ? prev.rank - row.rank : null;

                  return (
                    <tr key={row.month} style={{
                      background: i === monthlyRows.length - 1
                        ? (T.surface2 + "80") : "transparent",
                    }}>
                      <td style={{padding:"7px 8px",borderBottom:`0.5px solid ${T.border}`,
                        fontFamily:"'DM Mono',monospace",fontWeight: i === monthlyRows.length-1 ? 700 : 400}}>
                        {shortMonth(row.month)} {row.month.split("-")[0]}
                      </td>
                      <td style={{padding:"7px 8px",borderBottom:`0.5px solid ${T.border}`,
                        color: row.price === null ? T.textFaint : T.text,
                        fontWeight:600}}>
                        {row.price !== null ? `$${row.price.toFixed(2)}` : "N/A"}
                      </td>
                      <td style={{padding:"7px 8px",borderBottom:`0.5px solid ${T.border}`,
                        color: row.rank === null ? T.textFaint : T.text}}>
                        {row.rank !== null ? `#${row.rank}` : "N/A"}
                      </td>
                      <td style={{padding:"7px 8px",borderBottom:`0.5px solid ${T.border}`}}>
                        {i === 0 || (row.price === null && row.rank === null)
                          ? <span style={{color:T.textFaint}}>—</span>
                          : (
                            <div style={{display:"flex",flexDirection:"column",gap:1}}>
                              {priceDelta !== null && (
                                <span style={{fontSize:11,
                                  color: priceDelta > 0 ? "#f87171" : priceDelta < 0 ? "#4ade80" : T.textMuted}}>
                                  {priceDelta > 0 ? "+" : ""}{priceDelta.toFixed(2)} price
                                </span>
                              )}
                              {rankDelta !== null && (
                                <span style={{fontSize:11,
                                  color: rankDelta > 0 ? "#4ade80" : rankDelta < 0 ? "#f87171" : T.textMuted}}>
                                  {rankDelta > 0 ? `↑${rankDelta}` : rankDelta < 0 ? `↓${Math.abs(rankDelta)}` : "="} rank
                                </span>
                              )}
                              {priceDelta === null && rankDelta === null && (
                                <span style={{color:T.textFaint,fontSize:11}}>not ranked</span>
                              )}
                            </div>
                          )
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {(c.momentum !== null || c.stability !== null) && (
          <div style={{...S.card,marginBottom:16}}>
            <div style={S.h2}>Score Breakdown</div>
            <div style={{display:"flex",alignItems:"center"}}>
              <RadarChart width={160} height={140} data={radarData}>
                <PolarGrid stroke={T.border} />
                <PolarAngleAxis dataKey="metric" tick={{fontSize:9,fill:T.textMuted}} />
                <Radar dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
              </RadarChart>
              <div style={{flex:1}}>
                {[["Momentum",c.momentum,"Gaining popularity before price moves"],
                  ["Stability",c.stability,"Consistent demand month over month"],
                  ["Divergence",c.divergence,"Popularity rising faster than price"]].map(([label,val,desc])=>(
                  <div key={label} style={{marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                      <span style={{fontSize:12,fontWeight:500}}>{label}</span>
                      <span style={{fontSize:12,fontWeight:700,color:"#3b82f6"}}>{val?.toFixed(0)??'—'}</span>
                    </div>
                    <div style={{height:4,borderRadius:2,background:T.border,overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:2,background:"#3b82f6",
                        width:`${val??0}%`,transition:"width 0.3s"}}/>
                    </div>
                    <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main dashboard ──────────────────────────────────────────────────────────

  const byRank       = (a, b) => a.rank - b.rank;
  const byScore      = (a, b) => (b.master ?? 0) - (a.master ?? 0);
  const byPriceAsc   = (a, b) => (a.avg_price ?? 0) - (b.avg_price ?? 0);
  const byPriceDesc  = (a, b) => (b.avg_price ?? 0) - (a.avg_price ?? 0);

  const applySorter = (sortId) => {
    if (sortId === "rank")       return byRank;
    if (sortId === "score")      return byScore;
    if (sortId === "price_asc")  return byPriceAsc;
    if (sortId === "price_desc") return byPriceDesc;
    return byRank;
  };

  const buySignals = analytics
    .filter(c => {
      const v = buildVerdict(c).verdict;
      return v === "BUY" || v === "WATCH";
    })
    .sort(applySorter(buySort))
    .slice(0, 25);

  const opportunityPool = analytics.filter(c => c.master >= OPPORTUNITY_MIN_SCORE && c.marketPhase !== "in_freefall");
  const opportunities = [...opportunityPool]
    .sort((a, b) => b.master - a.master)
    .slice(0, 25)
    .sort(applySorter(oppSort));

  const dipPool = analytics.filter(c => buildVerdict(c).verdict === "DIP");
  const dips = [...dipPool]
    .sort(applySorter(dipSort))
    .slice(0, 25);

  const avoids = analytics
    .filter(c => buildVerdict(c).verdict === "AVOID")
    .sort(applySorter(avoSort))
    .slice(0, 25);

  const topMomentum = analytics
    .filter(c => c.momentum !== null)
    .sort((a,b) => (b.momentum??0)-(a.momentum??0))
    .slice(0, 25);

  const topStability = analytics
    .filter(c => c.stability !== null)
    .sort((a,b) => (b.stability??0)-(a.stability??0))
    .slice(0, 25);

  const TABS = [
    { id:"opportunities", label:"🔥 Opportunities" },
    { id:"dip",           label:"📉 Buy the Dip"   },
    { id:"divergence",    label:"💡 Buy Signals"   },
    { id:"avoids",        label:"🚫 Avoids"        },
    { id:"momentum",      label:"📈 Momentum"      },
    { id:"stability",     label:"🛡️ Stability"     },
    { id:"search",        label:"🔍 Search"        },
  ];

  return (
    <div style={containerStyle}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:16,fontWeight:700}}>Market Intelligence</div>
          <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
            {months.length > 0
              ? `${months.length} month${months.length>1?"s":""} of data · ${analytics.length} cards`
              : isAdmin ? "No data. upload a TCGPlayer report to start"
              : "We update this for you, check back soon if it's empty"}
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={{...S.btn("ghost"),padding:"8px 14px",fontSize:13}}
            onClick={()=>setShowLegend(true)}>
            ℹ️ How Scoring Works
          </button>
          {isAdmin && months.length > 0 && (
            <button style={{...S.btn("ghost"),padding:"8px 14px",fontSize:13}}
              onClick={()=>setView("upload")}>
              + Upload Report
            </button>
          )}
        </div>
      </div>

      {!hasMultipleMonths && analytics.length > 0 && (
        <div style={{...S.infoBox,marginBottom:16,borderColor:"#fbbf24"}}>
          <div style={{fontSize:12,color:"#fbbf24"}}>
            ⚠ Upload 2+ months of data to unlock Momentum and Divergence scoring.
            Currently showing Stability scores only.
          </div>
        </div>
      )}

      {analytics.length === 0 && (
        <div style={{...S.card,textAlign:"center",padding:"40px 20px"}}>
          <div style={{fontSize:36,marginBottom:12}}>📊</div>
          <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>No market data yet</div>
          <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>
            {isAdmin
              ? "Upload your monthly TCGPlayer reports to generate buying intelligence"
              : "We update this on your behalf every month, no action needed on your end"}
          </div>
          {isAdmin && (
            <button style={{...S.btn("primary"),padding:"12px 24px"}}
              onClick={()=>setView("upload")}>
              Upload TCGPlayer Reports
            </button>
          )}
        </div>
      )}

      {analytics.length > 0 && (
        <>
          <div style={{display:"flex",gap:6,marginBottom:16,overflowX:"auto",paddingBottom:4}}>
            {TABS.map(t => (
              <button key={t.id} style={{...S.chip(activeTab===t.id),
                padding:"8px 12px",whiteSpace:"nowrap",fontSize:12}}
                onClick={()=>setActiveTab(t.id)}>
                {t.label}
                {t.id==="divergence" && buySignals.length > 0 && (
                  <span style={{marginLeft:5,background:"#15803d",color:"#fff",
                    borderRadius:10,padding:"1px 6px",fontSize:10}}>
                    {buySignals.length}
                  </span>
                )}
                {t.id==="dip" && dipPool.length > 0 && (
                  <span style={{marginLeft:5,background:"#0f766e",color:"#fff",
                    borderRadius:10,padding:"1px 6px",fontSize:10}}>
                    {dipPool.length}
                  </span>
                )}
                {t.id==="avoids" && avoids.length > 0 && (
                  <span style={{marginLeft:5,background:"#dc2626",color:"#fff",
                    borderRadius:10,padding:"1px 6px",fontSize:10}}>
                    {avoids.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {activeTab==="opportunities" && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>Top Opportunities</div>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>
                    Top cards scoring {OPPORTUNITY_MIN_SCORE}+ · showing {opportunities.length} of {opportunityPool.length}
                  </div>
                </div>
              </div>
              <SortBar value={oppSort} onChange={setOppSort} T={T} />
              {opportunities.length === 0 && (
                <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>
                  No cards score high enough this month
                </div>
              )}
              {opportunities.map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={i+1}
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}

          {activeTab==="divergence" && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>Buy Signals</div>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>
                    BUY &amp; WATCH verdicts · top 25 by rank · {buySignals.length} found
                  </div>
                </div>
              </div>
              <SortBar value={buySort} onChange={setBuySort} T={T} />
              {!hasMultipleMonths && (
                <div style={{...S.infoBox,marginBottom:12,borderColor:"#fbbf24"}}>
                  <div style={{fontSize:12,color:"#fbbf24"}}>Upload 2+ months for full divergence analysis</div>
                </div>
              )}
              {buySignals.length === 0 && (
                <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>
                  No buy signals detected this month
                </div>
              )}
              {buySignals.map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={i+1} metric="divergence"
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}

          {activeTab==="dip" && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>Buy the Dip</div>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>
                    Popular Pokémon with a falling price · showing {dips.length} of {dipPool.length}
                  </div>
                </div>
              </div>
              <SortBar value={dipSort} onChange={setDipSort} T={T} />
              {dips.length === 0 && (
                <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>
                  No popular Pokémon in a price drop this month
                </div>
              )}
              {dips.map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={i+1}
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}

          {activeTab==="avoids" && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>Avoid List</div>
                  <div style={{fontSize:11,color:T.textMuted,marginBottom:8}}>
                    Cards with confirmed AVOID verdict · {avoids.length} found
                  </div>
                </div>
              </div>
              <SortBar value={avoSort} onChange={setAvoSort} T={T} />
              {avoids.length === 0 && (
                <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>
                  No avoid signals. looking good
                </div>
              )}
              {avoids.map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={i+1}
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}

          {activeTab==="momentum" && (
            <div>
              <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>Momentum</div>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>
                Cards gaining rank position. popularity rising before price catches up
              </div>
              {!hasMultipleMonths && (
                <div style={{...S.infoBox,marginBottom:12,borderColor:"#fbbf24"}}>
                  <div style={{fontSize:12,color:"#fbbf24"}}>Upload 2+ months for momentum scoring</div>
                </div>
              )}
              {topMomentum.map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={i+1} metric="momentum"
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}

          {activeTab==="stability" && (
            <div>
              <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>Stability</div>
              <div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>
                Cards with durable, consistent demand. safe inventory picks
              </div>
              {topStability.map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={i+1} metric="stability"
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}

          {activeTab==="search" && (
            <div>
              <input style={{...S.input,marginBottom:12,fontSize:15}}
                value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search card name or set…" autoFocus />
              {search.trim() && filtered.length === 0 && (
                <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>
                  No results for "{search}"
                </div>
              )}
              {filtered.slice(0,25).map((c,i) => (
                <CardRow key={c.product_name+c.report_month} card={c} rank={c.rank}
                  onSelect={()=>{setSelectedCard(c);setView("detail");}} S={S} T={T} />
              ))}
            </div>
          )}
        </>
      )}

      {showLegend && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) setShowLegend(false); }}>
          <div style={{...S.drawer, maxWidth:520, maxHeight:"85vh", overflowY:"auto"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16}}>
              <div style={{fontSize:18, fontWeight:800}}>How Scoring Works</div>
              <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={()=>setShowLegend(false)}>✕</button>
            </div>

            <div style={{fontSize:13, fontWeight:700, color:T.textMuted, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em"}}>
              Recommendation Tiers
            </div>
            {[
              ["🔥 Aggressive Buy", "#4ade80", "The strongest signal, high overall score with strong stability. Worth prioritizing."],
              ["✅ Strong Inventory", "#86efac", "A solid, well-rounded card worth holding or adding to."],
              ["📦 Stable Inventory", "#60a5fa", "Steady and reliable, nothing especially urgent either direction."],
              ["👀 Watchlist", "#fbbf24", "Mixed signals. Worth keeping an eye on before committing either way."],
              ["⛔ Avoid", "#f87171", "Weak score and declining demand. Not a good time to buy."],
            ].map(([label, color, desc]) => (
              <div key={label} style={{display:"flex", gap:10, marginBottom:10, alignItems:"flex-start"}}>
                <span style={{fontSize:13, fontWeight:700, color, flexShrink:0, minWidth:150}}>{label}</span>
                <span style={{fontSize:12, color:T.textMuted, lineHeight:1.5}}>{desc}</span>
              </div>
            ))}

            <div style={{fontSize:13, fontWeight:700, color:T.textMuted, margin:"20px 0 8px", textTransform:"uppercase", letterSpacing:"0.05em"}}>
              The Three Sub-Scores
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:13, fontWeight:700, marginBottom:2}}>📈 Momentum</div>
              <div style={{fontSize:12, color:T.textMuted, lineHeight:1.5}}>
                Is this card's rank (popularity) improving faster than its price is rising?
                A big jump in demand that price hasn't caught up to yet is a good sign.
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:13, fontWeight:700, marginBottom:2}}>🛡️ Stability</div>
              <div style={{fontSize:12, color:T.textMuted, lineHeight:1.5}}>
                How consistent has this card's ranking been over time? A steadily popular
                card scores higher than one that swings wildly month to month.
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:13, fontWeight:700, marginBottom:2}}>🔀 Divergence</div>
              <div style={{fontSize:12, color:T.textMuted, lineHeight:1.5}}>
                Has demand grown without price catching up yet? High divergence means the
                market may not have priced in the card's growing popularity.
              </div>
            </div>
            <div style={{fontSize:11, color:T.textFaint, marginBottom:16}}>
              The overall Master Score blends all three: 40% Momentum, 35% Stability, 25% Divergence.
            </div>

            <div style={{fontSize:13, fontWeight:700, color:T.textMuted, margin:"20px 0 8px", textTransform:"uppercase", letterSpacing:"0.05em"}}>
              Market Phase
            </div>
            {[
              ["📈 Rising", "Actively gaining rank and/or price."],
              ["📉 Post-Release Dip", "A new set's cards commonly dip shortly after launch, considered normal, not a red flag."],
              ["📊 Floor Forming", "Still declining, but the rate of decline is slowing, an early sign of bottoming out."],
              ["🔄 Recovering", "Price has turned back up from a recent low."],
              ["⚠️ In Freefall", "Declining for several months straight with no sign of slowing. The riskiest phase."],
              ["➡️ Mature / Stable", "Little recent movement either direction."],
            ].map(([label, desc]) => (
              <div key={label} style={{display:"flex", gap:10, marginBottom:8, alignItems:"flex-start"}}>
                <span style={{fontSize:12, fontWeight:700, flexShrink:0, minWidth:150}}>{label}</span>
                <span style={{fontSize:12, color:T.textMuted, lineHeight:1.5}}>{desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CARD ROW COMPONENT ───────────────────────────────────────────────────────
function CardRow({ card: c, rank, metric, onSelect, S, T }) {
  const [expanded, setExpanded] = useState(false);

  const score = metric === "momentum"  ? c.momentum
              : metric === "stability" ? c.stability
              : metric === "divergence"? c.divergence
              : c.master;

  const scoreColor = (score ?? 0) >= 75 ? "#15803d"
                   : (score ?? 0) >= 60 ? "#1d4ed8"
                   : (score ?? 0) >= 40 ? "#b45309"
                   : "#dc2626";

  const verdict = buildVerdict(c);
  const displayLabel = verdict.verdict;

  const verdictBg    = displayLabel === "BUY"   ? "rgba(21,128,61,0.08)"
                     : displayLabel === "WATCH" ? "rgba(59,130,246,0.08)"
                     : displayLabel === "HOLD"  ? "rgba(180,83,9,0.08)"
                     : displayLabel === "AVOID" ? "rgba(220,38,38,0.08)"
                     : displayLabel === "DIP"   ? "rgba(15,118,110,0.08)"
                     : "transparent";
  const verdictBorder = displayLabel === "BUY"   ? "#166534"
                      : displayLabel === "WATCH" ? "#1d4ed8"
                      : displayLabel === "HOLD"  ? "#92400e"
                      : displayLabel === "AVOID" ? "#991b1b"
                      : displayLabel === "DIP"   ? "#0f766e"
                      : T.border;

  const phaseMeta = PHASE_META[c.marketPhase] || PHASE_META.insufficient_data;

  return (
    <div style={{...S.card,marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}
        onClick={onSelect}>
        <div style={{width:28,height:28,borderRadius:6,background:T.border,
          display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:11,fontWeight:700,color:T.textMuted,flexShrink:0}}>
          #{c.rank}
        </div>

        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:600,overflow:"hidden",
            textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.product_name}</div>
          <div style={{fontSize:11,color:T.textMuted,display:"flex",gap:8,alignItems:"center"}}>
            <span>{c.set_name}</span>
            {c.rankChange !== null && (
              <span style={{color:c.rankChange>0?"#4ade80":c.rankChange<0?"#f87171":"#888",
                fontWeight:600}}>
                {c.rankChange>0?`↑${c.rankChange}`:c.rankChange<0?`↓${Math.abs(c.rankChange)}`:"="}
              </span>
            )}
            {c.opportunity && (
              <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",
                borderRadius:4,
                background: displayLabel==="BUY"  ?"#dcfce7":
                            displayLabel==="WATCH"?"#dbeafe":
                            displayLabel==="HOLD" ?"#fef9c3":"#fee2e2",
                color:      displayLabel==="BUY"  ?"#15803d":
                            displayLabel==="WATCH"?"#1d4ed8":
                            displayLabel==="HOLD" ?"#b45309":"#dc2626"}}>
                {displayLabel}
              </span>
            )}
            {c.marketPhase && c.marketPhase !== "insufficient_data" && (
              <span style={{fontSize:9,color:T.textFaint}}>
                {phaseMeta.emoji} {phaseMeta.label}
              </span>
            )}
            {c.pokemonTier?.tier === 1 && (
              <span style={{fontSize:9,color:"#f59e0b",fontWeight:700}}>⭐T1</span>
            )}
          </div>
        </div>

        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontSize:13,fontWeight:700,color:"#3b82f6"}}>
            ${c.avg_price?.toFixed(2)}
          </div>
          <div style={{fontSize:12,fontWeight:700,color:scoreColor}}>
            {score?.toFixed(0) ?? "—"}
          </div>
          <div style={{fontSize:9,color:T.textFaint}}>score</div>
        </div>
      </div>

      <div style={{height:3,borderRadius:2,background:T.border,overflow:"hidden",marginTop:8}}>
        <div style={{height:"100%",borderRadius:2,background:scoreColor,
          width:`${score??0}%`,transition:"width 0.3s"}}/>
      </div>

      <button
        style={{
          marginTop:8, padding:"4px 0", background:"none", border:"none",
          cursor:"pointer", display:"flex", alignItems:"center", gap:4,
          color:T.textMuted, fontSize:11, fontFamily:"'Sora',sans-serif",
        }}
        onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}>
        <span style={{fontSize:10, transition:"transform 0.15s",
          display:"inline-block", transform: expanded ? "rotate(90deg)" : "rotate(0deg)"}}>▶</span>
        {expanded ? "Hide reasoning" : "Why this rating?"}
      </button>

      {expanded && (
        <div style={{
          marginTop:6, padding:"10px 12px", borderRadius:8,
          background: verdictBg,
          border: `1px solid ${verdictBorder}`,
        }}>
          {verdict.lines.map((line, i) => (
            <div key={i} style={{
              display:"flex", gap:8, alignItems:"flex-start",
              marginBottom: i < verdict.lines.length - 1 ? 8 : 0,
            }}>
              <span style={{fontSize:11, color: verdictBorder, flexShrink:0, marginTop:1}}>•</span>
              <span style={{fontSize:12, color:T.text, lineHeight:1.5}}>{line}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
